import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Page slice for `platform-tenant-detail` (state-shape §2 `platform-tenant-detail-page`).
 *
 * UC-10 (Tenant — Suspend) introduces the Suspend-confirm subset.
 * UC-11 (Tenant — Reactivate) introduces the Reactivate-confirm subset.
 * UC-15 (Override Workspace Domain) introduces the override-modal subset.
 * UC-12 (Schedule Deletion) introduces the scheduleDeletion subset.
 * UC-13 (Cancel Scheduled Deletion) introduces the cancelDeletion subset.
 */
export type PlatformTenantDetailPageState = {
  // UC-10 — Suspend
  pendingSuspendConfirm: boolean;
  // UC-11 — Reactivate
  pendingReactivateConfirm: boolean;
  // UC-15 — Override Workspace Domain Allow-List
  overrideWorkspaceDomainModalOpen: boolean;
  formSeedWorkspaceDomains: string[];
  formAllowedWorkspaceDomains: string[];
  // UC-12 — Schedule Deletion
  scheduleDeletionModalOpen: boolean;
  scheduleDeletionDraft: { acknowledgement: boolean; typedConfirmSlug: string };
  // UC-13 — Cancel Scheduled Deletion
  pendingCancelDeletionConfirm: boolean;
  // Shared (scoped to the active modal)
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const initialState: PlatformTenantDetailPageState = {
  pendingSuspendConfirm: false,
  pendingReactivateConfirm: false,
  overrideWorkspaceDomainModalOpen: false,
  formSeedWorkspaceDomains: [],
  formAllowedWorkspaceDomains: [],
  scheduleDeletionModalOpen: false,
  scheduleDeletionDraft: { acknowledgement: false, typedConfirmSlug: '' },
  pendingCancelDeletionConfirm: false,
  submitInProgress: false,
  lastErrorKey: null,
};

export const platformTenantDetailPageSlice = createSlice({
  name: 'platformTenantDetailPage',
  initialState,
  reducers: {
    // ── UC-10 Suspend ──────────────────────────────────────────────────────────
    openSuspendConfirm(state) {
      state.pendingSuspendConfirm = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeSuspendConfirm(state) {
      state.pendingSuspendConfirm = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-11 Reactivate ──────────────────────────────────────────────────────
    openReactivateConfirm(state) {
      state.pendingReactivateConfirm = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeReactivateConfirm(state) {
      state.pendingReactivateConfirm = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-15 Override Workspace Domain ───────────────────────────────────────
    openOverrideWorkspaceDomainModal(
      state,
      action: PayloadAction<{ seed: string[]; allowed: string[] }>,
    ) {
      state.overrideWorkspaceDomainModalOpen = true;
      state.formSeedWorkspaceDomains = [...action.payload.seed];
      state.formAllowedWorkspaceDomains = [...action.payload.allowed];
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeOverrideWorkspaceDomainModal(state) {
      state.overrideWorkspaceDomainModalOpen = false;
      state.formSeedWorkspaceDomains = [];
      state.formAllowedWorkspaceDomains = [];
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setSeedWorkspaceDomains(state, action: PayloadAction<string[]>) {
      state.formSeedWorkspaceDomains = action.payload;
      // Enforce allowed ⊆ seed: remove any allowed domain no longer in seed
      const seedSet = new Set(action.payload);
      state.formAllowedWorkspaceDomains = state.formAllowedWorkspaceDomains.filter((d) =>
        seedSet.has(d),
      );
    },
    setAllowedWorkspaceDomains(state, action: PayloadAction<string[]>) {
      state.formAllowedWorkspaceDomains = action.payload;
    },
    // ── UC-12 Schedule Deletion ───────────────────────────────────────────────
    openScheduleDeletionModal(state) {
      state.scheduleDeletionModalOpen = true;
      state.scheduleDeletionDraft = { acknowledgement: false, typedConfirmSlug: '' };
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeScheduleDeletionModal(state) {
      state.scheduleDeletionModalOpen = false;
      state.scheduleDeletionDraft = { acknowledgement: false, typedConfirmSlug: '' };
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setScheduleDeletionAcknowledgement(state, action: PayloadAction<boolean>) {
      state.scheduleDeletionDraft.acknowledgement = action.payload;
    },
    setScheduleDeletionTypedSlug(state, action: PayloadAction<string>) {
      state.scheduleDeletionDraft.typedConfirmSlug = action.payload;
    },
    // ── UC-13 Cancel Scheduled Deletion ───────────────────────────────────────
    openCancelDeletionConfirm(state) {
      state.pendingCancelDeletionConfirm = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeCancelDeletionConfirm(state) {
      state.pendingCancelDeletionConfirm = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── Shared ────────────────────────────────────────────────────────────────
    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
    resetPage() {
      return initialState;
    },
  },
});

export const {
  openSuspendConfirm,
  closeSuspendConfirm,
  openReactivateConfirm,
  closeReactivateConfirm,
  openOverrideWorkspaceDomainModal,
  closeOverrideWorkspaceDomainModal,
  setSeedWorkspaceDomains,
  setAllowedWorkspaceDomains,
  openScheduleDeletionModal,
  closeScheduleDeletionModal,
  setScheduleDeletionAcknowledgement,
  setScheduleDeletionTypedSlug,
  openCancelDeletionConfirm,
  closeCancelDeletionConfirm,
  setSubmitInProgress,
  setLastErrorKey,
  resetPage,
} = platformTenantDetailPageSlice.actions;

type AppRootState = {
  platformTenantDetailPage: PlatformTenantDetailPageState;
};

export const selectPlatformTenantDetailPage = (state: AppRootState) =>
  state.platformTenantDetailPage;
